/**
 * components/settings/editor-sections.ts
 *
 * The strategy editor's section table, kept out of the component so tests and
 * the wizard-parity checks can read it without mounting the editor.
 */

import type { LucideIcon } from "lucide-react";
import { en, type Translations } from "../../i18n/en";
import { PRICING_SECTION_ICONS, type PricingSectionIconKey } from "../pricing/section-icons";
import type { SettingsTier } from "../pricing/settings-tier";
import type { StrategySectionGroup } from "../pricing/strategy-section-groups";
import {
    STRATEGY_SECTION_REGISTRY,
    type StrategySectionId,
} from "../pricing/strategy-section-registry";

type StrategyParamsCopy = Translations["strategyParams"];

/**
 * Every section of the editor, in the order the navigator lists them and
 * Předchozí / Další walks them, so a new section only has to be listed once.
 *
 * The tier decides whether a section is on screen at all: `expert` sections
 * ship with defaults that already price correctly, so they stay behind the
 * expert-mode switch rather than padding out a first pass. The wizard tiers the
 * same ground in `wizardSteps.ts`, and `settings-tier.test.ts` holds the two in
 * agreement.
 *
 * `group` files the section under one of the five groups the navigator heads
 * its runs with (`strategy-section-groups.ts`) — a cut of this order, never a
 * reshuffle of it. `slug` is its address, `/strategies/:id/<slug>`: what a
 * bookmark, the setup alert and a support reply link to, so it stays put when
 * a title is reworded. `titleKey` is the `strategyParams` string the section
 * is named by, everywhere it is named.
 *
 * `expertFields` marks an essential section that still holds expert-tier
 * controls inside it. Those sections carry their own reveal, so wanting one
 * advanced dial costs the operator that section rather than the whole
 * essential view.
 *
 * `saves` marks a section whose changes are not part of the version "Save and
 * activate" mints: Room Kind Structure writes the connection's room-kind
 * overlay through its own button, and the two distribution sections write the
 * connection's settings as they are edited. Each says so in its header.
 *
 * `icon` is the glyph the navigator row wears, and the same one the wizard
 * puts on the step that edits it — see `section-icons.ts` for why a section
 * carries one at all, and why no two of them may be the same.
 */
export const EDITOR_SECTIONS = [
    // Naming, renumbering and hiding the PMS's room kinds is the editor's
    // counterpart to the wizard's property-identity step: it establishes what
    // the property has before any of it can be priced. That step sits outside
    // the canonical pricing order for the same reason, so this shell carries
    // no section and simply leads.
    { key: "roomKindStructure", tier: "essential", icon: "roomKindStructure", group: "foundation", slug: "room-kinds", titleKey: "roomKindStructureTitle", saves: "separately" },
    { key: "strategy", tier: "essential", icon: "strategyPosture", expertFields: true, section: "strategyPosture", group: "foundation", slug: "strategy-posture", titleKey: "strategyPosture" },
    { key: "marketInfluence", tier: "expert", icon: "marketInfluence", section: "marketInfluence", group: "foundation", slug: "market", titleKey: "marketInfluenceTitle" },
    // Ahead of the price band it has to fit inside — see the note on
    // `CANONICAL_SECTION_ORDER`.
    { key: "distribution", tier: "essential", icon: "dayAdjustments", expertFields: true, section: "dayAdjustments", group: "prices", slug: "day-adjustments", titleKey: "distribution" },
    { key: "roomKindOverrides", tier: "essential", icon: "keyMetrics", expertFields: true, section: "keyMetrics", group: "prices", slug: "room-type-prices", titleKey: "roomKindStrategyOverridesTitle" },
    { key: "roomStructure", tier: "essential", icon: "roomStructure", expertFields: true, section: "propertyStructure", group: "prices", slug: "guest-count", titleKey: "roomStructure" },
    { key: "bookingWindow", tier: "expert", icon: "bookingWindow", section: "propertyStructure", group: "prices", slug: "booking-window", titleKey: "bookingWindowTitle" },
    { key: "priceGranularity", tier: "expert", icon: "priceGranularity", section: "priceGranularity", group: "prices", slug: "price-granularity", titleKey: "priceGranularity" },
    { key: "rateRestrictions", tier: "expert", icon: "rateRestrictions", section: "rateRestrictions", group: "sellingRules", slug: "minimum-stay", titleKey: "losRestrictions" },
    { key: "lastMinute", tier: "expert", icon: "lastMinute", section: "lastMinute", group: "sellingRules", slug: "last-minute", titleKey: "lastMinuteTitle" },
    { key: "gapFilling", tier: "expert", icon: "gapFilling", section: "lastMinute", group: "sellingRules", slug: "gap-filling", titleKey: "gapFillingTitle" },
    // Expert for a property whose seasons were measured; shown to one whose
    // were not — the wizard declares the same, see `isSettingVisible`.
    { key: "seasonality", tier: "expert", icon: "seasonality", section: "seasonality", essentialWhenUnmeasured: true, group: "calendar", slug: "seasonality", titleKey: "seasonality" },
    { key: "holidays", tier: "essential", icon: "holidays", section: "specialDates", group: "calendar", slug: "special-dates", titleKey: "holidays" },
    { key: "bridgeDays", tier: "expert", icon: "bridgeDays", section: "specialDates", group: "calendar", slug: "bridge-days", titleKey: "bridgeDaysTitle" },
    // No wizard counterpart: both are distribution plumbing the wizard leaves
    // to the editor entirely, so they sit after the sections that mirror it.
    { key: "channelMapping", tier: "expert", icon: "channelMapping", group: "distribution", slug: "channel-plans", titleKey: "channelMappingTitle", saves: "immediately" },
    { key: "nonRefundable", tier: "expert", icon: "nonRefundable", group: "distribution", slug: "non-refundable", titleKey: "nonRefundableTitle", saves: "immediately" },
] as const satisfies readonly {
    key: string;
    tier: SettingsTier;
    icon: PricingSectionIconKey;
    expertFields?: boolean;
    /** Expert-tier, but shown while the analysis has not measured its section — see `isSettingVisible`. */
    essentialWhenUnmeasured?: boolean;
    /** The pricing section this shell edits, when the wizard has one for it. */
    section?: StrategySectionId;
    group: StrategySectionGroup;
    slug: string;
    titleKey: keyof StrategyParamsCopy;
    /** Saved outside the strategy version — see above. */
    saves?: "separately" | "immediately";
}[];

export type EditorSectionKey = (typeof EDITOR_SECTIONS)[number]["key"];

export const EDITOR_SECTION_KEYS: readonly EditorSectionKey[] = EDITOR_SECTIONS.map((s) => s.key);

/** Each shell's table entry by its key, for a caller that needs more than the tier. */
export const EDITOR_SECTION_BY_KEY = Object.fromEntries(
    EDITOR_SECTIONS.map((s) => [s.key, s]),
) as Record<EditorSectionKey, (typeof EDITOR_SECTIONS)[number]>;

export const EDITOR_SECTION_TIERS = Object.fromEntries(
    EDITOR_SECTIONS.map((s) => [s.key, s.tier]),
) as Record<EditorSectionKey, SettingsTier>;

/**
 * The glyph each navigator row wears. Derived rather than written out a second
 * time, so a section cannot be listed above with one icon and drawn with
 * another.
 */
export const EDITOR_SECTION_ICONS = Object.fromEntries(
    EDITOR_SECTIONS.map((s) => [s.key, PRICING_SECTION_ICONS[s.icon]]),
) as Record<EditorSectionKey, LucideIcon>;

export const EDITOR_SECTION_EXPERT_FIELDS = Object.fromEntries(
    EDITOR_SECTIONS.map((s) => [s.key, "expertFields" in s && s.expertFields === true]),
) as Record<EditorSectionKey, boolean>;

/** How many sections the essential view is holding back, for the toggle hint. */
export const EXPERT_EDITOR_SECTION_COUNT = EDITOR_SECTIONS
    .filter((s) => s.tier === "expert").length;

const SECTION_BY_SLUG: ReadonlyMap<string, EditorSectionKey> = new Map(
    EDITOR_SECTIONS.map((s) => [s.slug, s.key]),
);

/** The section an address names, or undefined for a slug no section answers to. */
export function editorSectionFromSlug(slug: string): EditorSectionKey | undefined {
    return SECTION_BY_SLUG.get(slug);
}

/**
 * Every section's name, from whatever part of `strategyParams` the caller
 * has — defaulted key by key from English, since a partial `t` is what the
 * test harnesses hand the editor.
 */
export function editorSectionTitles(sp: Partial<StrategyParamsCopy>): Record<EditorSectionKey, string> {
    return Object.fromEntries(
        EDITOR_SECTIONS.map((s) => {
            const title = sp[s.titleKey] ?? en.strategyParams[s.titleKey];
            return [s.key, typeof title === "string" ? title : s.key];
        }),
    ) as Record<EditorSectionKey, string>;
}

/**
 * Params set in a sibling shell of the section the registry files them under:
 * the booking window beside guest count pricing, gap filling beside
 * last-minute, bridge and shoulder days beside the holiday list. Pickup is a
 * block inside Strategy Posture that the registry does not list, and the bed
 * counts are edited in Room Kind Structure, which writes them into the params
 * for Guest Count Pricing to read.
 */
const SHELL_OVERRIDES: Readonly<Record<string, EditorSectionKey>> = {
    roomKindLeadTimes: "bookingWindow",
    peakNightBookingWindowDays: "bookingWindow",
    roomKindCapacities: "roomKindStructure",
    gapFillingEnabled: "gapFilling",
    gapFillingMaxGapDays: "gapFilling",
    gapFillingAdjustmentPct: "gapFilling",
    bridgeDayPremiumEnabled: "bridgeDays",
    bridgeDayPremiumMultiplier: "bridgeDays",
    shoulderDayEnabled: "bridgeDays",
    shoulderDayAdjustmentPct: "bridgeDays",
    pickupInfluenceEnabled: "strategy",
    pickupInfluenceStrength: "strategy",
    pickupWindowDays: "strategy",
    minRoomsForRoomKindSignal: "strategy",
};

/** The first shell that edits a pricing section. */
function shellOfSection(sectionId: StrategySectionId): EditorSectionKey | null {
    const shell = EDITOR_SECTIONS.find((entry) => "section" in entry && entry.section === sectionId);
    return shell ? shell.key : null;
}

const FIELD_SHELLS: ReadonlyMap<string, EditorSectionKey> = new Map([
    ...STRATEGY_SECTION_REGISTRY.flatMap((section) => {
        const shell = shellOfSection(section.id);
        return shell ? section.fields.map((field) => [field, shell] as [string, EditorSectionKey]) : [];
    }),
    ...Object.entries(SHELL_OVERRIDES),
]);

/**
 * The section that sets a top-level param, or null for one no section sets
 * (the currency, the timezone). A strategy proposal files its changes by it,
 * and the editor falls back on it to say which section holds a change made
 * from no section at all — a seeded price, an answered question.
 */
export function editorSectionOfParam(field: string): EditorSectionKey | null {
    return FIELD_SHELLS.get(field) ?? null;
}

/** What the editor can show: the strategy's summary, or one of its sections. */
export type EditorPage = EditorSectionKey | "summary";
